import { useCallback, useLayoutEffect, useRef, type ChangeEvent, type KeyboardEvent } from 'react';

import { cn } from '@/lib/utils';

export interface ComposerTextAreaProps {
  /** Current value of the textarea */
  value: string;
  /** Called when the value changes */
  onChange: (value: string) => void;
  /** Called when user submits */
  onSubmit?: () => void;
  /** Placeholder text */
  placeholder?: string;
  /** Disable the textarea */
  disabled?: boolean;
  /** Maximum height before scrolling (default: 200px) */
  maxHeight?: number;
  /** Additional class names */
  className?: string;
}

/**
 * ComposerTextArea — auto-growing textarea for message input.
 *
 * Features:
 * - Auto-grows with content up to maxHeight
 * - Enter to submit
 * - Shift + Enter for newline
 * - Accessible placeholder and focus states
 *
 * @example
 * ```tsx
 * <ComposerTextArea
 *   value={draft}
 *   onChange={setDraft}
 *   onSubmit={handleSend}
 *   placeholder="Ask the copilot anything..."
 * />
 * ```
 */
export function ComposerTextArea({
  value,
  onChange,
  onSubmit,
  placeholder = 'Type a message...',
  disabled = false,
  maxHeight = 200,
  className,
}: ComposerTextAreaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set to scrollHeight, but cap at maxHeight
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;
    }
  }, [maxHeight]);

  // Keep height in sync when value is programmatically changed (e.g. after send clears draft).
  useLayoutEffect(() => {
    adjustHeight();
  }, [adjustHeight, value]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
    adjustHeight();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Don't treat IME composition as a submit.
    if ('isComposing' in e.nativeEvent && e.nativeEvent.isComposing) {
      return;
    }

    // Submit on Enter (Shift+Enter keeps newline)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit?.();
    }
  };

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      disabled={disabled}
      rows={2}
      className={cn(
        'w-full resize-none border-0 bg-transparent text-xs font-medium',
        'placeholder:text-muted-foreground',
        'outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      style={{ maxHeight: `${maxHeight}px` }}
    />
  );
}
